import React, {useMemo} from 'react';
import {useTable, usePagination, useFilters} from 'react-table';
import {Table, Button, Dropdown, Input, Grid} from 'semantic-ui-react';
import {
  Filter,
  DefaultColumnFilter,
} from '../../../common/components/tableFilters';
import {getNumericValue} from '../../../common/util/fieldsValidation';
import {invoicesPageSizes} from '../../../common/constants/lists';

const columnsTableWithProps = (detailName) => {
  const columns = [
    {
      'Header': 'NÚMERO DE ORDEN',
      'accessor': 'orderId',
    },
    {
      'Header': 'CHASSIS',
      'accessor': 'chassis',
    },
    {
      'Header': 'KATASHIKI',
      'accessor': 'katashiki',
    },
    {
      'Header': 'MODELO',
      'accessor': 'model',
    },
  ];

  if (
    detailName !== 'ACCESORIOS' &&
    !columns.some((column) => column.accessor === 'taskId')
  ) {
    const tasksInfo = [
      {
        'Header': 'NÚMERO DE TAREA',
        'accessor': 'taskId',
      },
      {
        'Header': 'DESCRIPCIÓN TAREA',
        'accessor': 'taskDescription',
      },
      {
        'Header': 'RESPONSABLE',
        'accessor': 'responsible',
        'Footer': 'SUBTOTAL',
      },
      {
        'Header': 'PRECIO',
        'accessor': 'totalPrice',
        'Cell': ({row}) => `$${getNumericValue(row.original.totalPrice, 2)}`,
        'Footer': ({page}) => {
          const total = page.reduce(
            (accum, row) => (accum += row.original.totalPrice),
            0
          );

          return `$${getNumericValue(total, 2)}`;
        },
      },
      {
        'Header': 'OBSERVACIÓN',
        'accessor': 'observation',
      },
    ];

    return columns.concat(tasksInfo);
  }

  return columns;
};

const TablePaginator = ({
  page,
  canPreviousPage,
  pageIndex,
  pageOptions,
  pageSize,
  canNextPage,
  nextPage,
  gotoPage,
  previousPage,
  setPageSize,
}) => {
  if (page.length) {
    return (
      <Grid className='pagination'>
        <Grid.Row>
          <Grid.Column width={4} textAlign='left'>
            <Button
              className='navigation-button'
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
              content='Anterior'
            />
          </Grid.Column>

          <Grid.Column width={4} textAlign='right'>
            <span>páginas </span>
            <span>
              <Input
                className='pages-input'
                type='number'
                value={pageIndex + 1}
                min='1'
                max={pageOptions.length}
                onChange={(e, {value}) => {
                  const page = value ? Number(value) - 1 : 0;

                  if (page < 1 || page > pageOptions.length) {
                    e.target.value = 1;
                  }

                  gotoPage(page);
                }}
              />
            </span>{' '}
            <span>de {pageOptions.length}</span>
          </Grid.Column>

          <Grid.Column width={4} textAlign='left'>
            <Dropdown
              className='page-sizes-selection'
              selection
              upward
              compact
              value={pageSize}
              options={invoicesPageSizes}
              onChange={(e, {value}) => {
                setPageSize(Number(value));
              }}
            />

            <span> filas</span>
          </Grid.Column>

          <Grid.Column width={4} textAlign='right'>
            <Button
              className='navigation-button'
              onClick={() => nextPage()}
              disabled={!canNextPage}
              content='Siguiente'
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }

  return null;
};

const IndiceDetailTable = ({detailName, dataTable}) => {
  const columnsTable = columnsTableWithProps(detailName);
  const data = useMemo(() => dataTable, []);
  const columns = useMemo(() => columnsTable, []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    'state': {pageIndex, pageSize},
  } = useTable(
    {
      columns,
      data,
      'defaultColumn': {'Filter': DefaultColumnFilter},
      'initialState': {'pageIndex': 0, 'pageSize': 10},
    },
    useFilters,
    usePagination
  );

  return (
    <>
      <div className='table-container'>
        <Table className='interactive' compact striped {...getTableProps()}>
          <Table.Header>
            {headerGroups.map((headerGroup) => (
              <Table.Row key='headers' {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, index) => (
                  <Table.HeaderCell
                    key={`title-${index}`}
                    textAlign='center'
                    {...column.getHeaderProps()}
                  >
                    {column.render('Header')}
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            ))}

            {headerGroups.map((headerGroup) => (
              <Table.Row key='filters' id='filters'>
                {headerGroup.headers.map((column, index) => (
                  <Table.HeaderCell key={`filter-${index}`} textAlign='center'>
                    <Filter column={column} />
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            ))}
          </Table.Header>

          <Table.Body {...getTableBodyProps()}>
            {!page.length && (
              <Table.Row>
                <Table.Cell colSpan={15} textAlign='center'>
                  No hay datos disponibles
                </Table.Cell>
              </Table.Row>
            )}

            {page.length > 0 &&
              page.map((row, i) => {
                prepareRow(row);

                return (
                  <Table.Row key={i}>
                    {row.cells.map((cell, idx) => {
                      return (
                        <Table.Cell key={idx} textAlign='center'>
                          {cell.render('Cell')}
                        </Table.Cell>
                      );
                    })}
                  </Table.Row>
                );
              })}
          </Table.Body>

          {detailName !== 'ACCESORIOS' && (
            <Table.Footer>
              {footerGroups.map((group) => (
                <Table.Row {...group.getFooterGroupProps()}>
                  {group.headers.map((column) => (
                    <Table.HeaderCell
                      {...column.getFooterProps()}
                      textAlign='center'
                    >
                      {column.render('Footer')}
                    </Table.HeaderCell>
                  ))}
                </Table.Row>
              ))}
            </Table.Footer>
          )}
        </Table>
      </div>

      {data.length > pageSize && (
        <TablePaginator
          page={page}
          canPreviousPage={canPreviousPage}
          pageIndex={pageIndex}
          pageOptions={pageOptions}
          pageSize={pageSize}
          canNextPage={canNextPage}
          nextPage={nextPage}
          gotoPage={gotoPage}
          previousPage={previousPage}
          setPageSize={setPageSize}
        />
      )}
    </>
  );
};

export default IndiceDetailTable;
